import { useState, useMemo, useEffect, useRef, Fragment } from "react"
import { motion } from "motion/react"
import { Input } from "@/components/ui/input"
import { PieChart } from "@/components/mortgage/PieChart"
import { AnimatedNumber } from "@/components/mortgage/AnimatedNumber"
import { NumberPlusMinus } from "@/components/ui/number-plus-minus"
import { CheckboxDiv } from "@/components/ui/checkbox-div"
import { Calendar, CalendarDays } from "lucide-react"
import { useDebounce } from "@/MyCode/hooks/useDebounce"

interface PaymentSchedule {
  month: number
  date: string
  year: number
  payment: number
  principal: number
  interest: number
  balance: number
}

interface YearGroup {
  year: number
  payments: PaymentSchedule[]
  totals: {
    payment: number
    principal: number
    interest: number
    endingBalance: number
  }
}

export function MortgageCalculator() {
  // Get current month and year
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1

  const [loanAmount, setLoanAmount] = useState(300000)
  const [interestRate, setInterestRate] = useState(4.5)
  const [loanTerm, setLoanTerm] = useState(30)
  const [startMonth, setStartMonth] = useState(currentMonth)
  const [startYear, setStartYear] = useState(currentYear)
  const [propertyTax, setPropertyTax] = useState(3000)
  const [homeInsurance, setHomeInsurance] = useState(1200)
  const [otherCosts, setOtherCosts] = useState(500)
  const [scheduleView, setScheduleView] = useState<Set<string>>(new Set(["months"]))
  const [extraMonthlyPay, setExtraMonthlyPay] = useState(0)
  const [extraMonthlyFromMonth, setExtraMonthlyFromMonth] = useState(currentMonth)
  const [extraMonthlyFromYear, setExtraMonthlyFromYear] = useState(currentYear)
  const [extraYearlyPay, setExtraYearlyPay] = useState(0)
  const [extraYearlyFromMonth, setExtraYearlyFromMonth] = useState(currentMonth)
  const [extraYearlyFromYear, setExtraYearlyFromYear] = useState(currentYear)
  const [extraOneTimePay, setExtraOneTimePay] = useState(0)

  // Debounced values for form inputs (500ms delay)
  const debouncedLoanAmount = useDebounce(loanAmount, 500)
  const debouncedInterestRate = useDebounce(interestRate, 500)
  const debouncedLoanTerm = useDebounce(loanTerm, 500)
  const debouncedPropertyTax = useDebounce(propertyTax, 500)
  const debouncedHomeInsurance = useDebounce(homeInsurance, 500)
  const debouncedOtherCosts = useDebounce(otherCosts, 500)
  const debouncedExtraMonthlyPay = useDebounce(extraMonthlyPay, 500)
  const debouncedExtraYearlyPay = useDebounce(extraYearlyPay, 500)
  const debouncedExtraOneTimePay = useDebounce(extraOneTimePay, 500)

  // Generate year options (current year ± 50 years)
  const yearOptions = Array.from({ length: 101 }, (_, i) => currentYear - 50 + i)

  // Month names
  const monthNames = [
    // "January", "February", "March", "April", "May", "June",
    // "July", "August", "September", "October", "November", "December"
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ]

  const calculateMortgage = useMemo(() => {
    const principal = debouncedLoanAmount
    const monthlyRate = debouncedInterestRate / 100 / 12
    const numberOfPayments = debouncedLoanTerm * 12

    if (monthlyRate === 0) {
      const monthlyPayment = principal / numberOfPayments
      return {
        monthlyPayment,
        totalPayment: monthlyPayment * numberOfPayments,
        totalInterest: 0,
      }
    }

    const monthlyPayment =
      (principal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
      (Math.pow(1 + monthlyRate, numberOfPayments) - 1)

    const totalPayment = monthlyPayment * numberOfPayments
    const totalInterest = totalPayment - principal

    return {
      monthlyPayment,
      totalPayment,
      totalInterest,
    }
  }, [debouncedLoanAmount, debouncedInterestRate, debouncedLoanTerm])

  const paymentSchedule: PaymentSchedule[] = useMemo(() => {
    const schedule: PaymentSchedule[] = []
    const monthlyRate = debouncedInterestRate / 100 / 12
    const numberOfPayments = debouncedLoanTerm * 12
    let balance = debouncedLoanAmount

    // Helper function to format date and get year
    const formatDate = (monthOffset: number) => {
      let year = startYear
      let month = startMonth + monthOffset - 1

      // Adjust year if month exceeds 12
      while (month > 12) {
        month -= 12
        year += 1
      }

      const date = new Date(year, month - 1, 1)
      return {
        formatted: date.toLocaleDateString("en-US", { month: "short", year: "numeric" }),
        year: year,
        month: month,
      }
    }

    // Helper function to check if extra monthly payment applies
    const shouldApplyExtraMonthly = (monthOffset: number) => {
      const dateInfo = formatDate(monthOffset)
      const startDate = new Date(extraMonthlyFromYear, extraMonthlyFromMonth - 1, 1)
      const currentDate = new Date(dateInfo.year, dateInfo.month - 1, 1)
      return currentDate >= startDate && debouncedExtraMonthlyPay > 0
    }

    // Helper function to check if extra yearly payment applies
    const shouldApplyExtraYearly = (monthOffset: number) => {
      const dateInfo = formatDate(monthOffset)
      const startDate = new Date(extraYearlyFromYear, extraYearlyFromMonth - 1, 1)
      const currentDate = new Date(dateInfo.year, dateInfo.month - 1, 1)
      // Apply yearly payment in the same month each year
      if (currentDate >= startDate && debouncedExtraYearlyPay > 0) {
        const monthsSinceStart = (dateInfo.year - extraYearlyFromYear) * 12 + (dateInfo.month - extraYearlyFromMonth)
        return monthsSinceStart >= 0 && monthsSinceStart % 12 === 0
      }
      return false
    }

    // Apply one-time payment at the start
    if (debouncedExtraOneTimePay > 0) {
      balance = Math.max(0, balance - debouncedExtraOneTimePay)
    }

    if (monthlyRate === 0) {
      const monthlyPayment = balance / numberOfPayments
      for (let month = 1; month <= numberOfPayments && balance > 0.01; month++) {
        const interest = 0
        let principal = monthlyPayment
        let extraPrincipal = 0

        // Apply extra monthly payment
        if (shouldApplyExtraMonthly(month)) {
          extraPrincipal += debouncedExtraMonthlyPay
        }

        // Apply extra yearly payment
        if (shouldApplyExtraYearly(month)) {
          extraPrincipal += debouncedExtraYearlyPay
        }

        principal += extraPrincipal
        balance -= principal
        const dateInfo = formatDate(month)
        schedule.push({
          month,
          date: dateInfo.formatted,
          year: dateInfo.year,
          payment: monthlyPayment + extraPrincipal,
          principal,
          interest,
          balance: Math.max(0, balance),
        })
      }
    } else {
      const monthlyPayment = calculateMortgage.monthlyPayment
      for (let month = 1; month <= numberOfPayments && balance > 0.01; month++) {
        const interest = balance * monthlyRate
        let principal = monthlyPayment - interest
        let extraPrincipal = 0

        // Apply extra monthly payment
        if (shouldApplyExtraMonthly(month)) {
          extraPrincipal += debouncedExtraMonthlyPay
        }

        // Apply extra yearly payment
        if (shouldApplyExtraYearly(month)) {
          extraPrincipal += debouncedExtraYearlyPay
        }

        principal += extraPrincipal
        balance -= principal
        const dateInfo = formatDate(month)
        schedule.push({
          month,
          date: dateInfo.formatted,
          year: dateInfo.year,
          payment: monthlyPayment + extraPrincipal,
          principal,
          interest,
          balance: Math.max(0, balance),
        })
      }
    }

    return schedule
  }, [
    debouncedLoanAmount,
    debouncedInterestRate,
    debouncedLoanTerm,
    calculateMortgage.monthlyPayment,
    startMonth,
    startYear,
    debouncedExtraMonthlyPay,
    extraMonthlyFromMonth,
    extraMonthlyFromYear,
    debouncedExtraYearlyPay,
    extraYearlyFromMonth,
    extraYearlyFromYear,
    debouncedExtraOneTimePay,
  ])

  // Group payments by year and calculate totals
  const yearGroups: YearGroup[] = useMemo(() => {
    const groups: { [year: number]: PaymentSchedule[] } = {}

    paymentSchedule.forEach((payment) => {
      if (!groups[payment.year]) {
        groups[payment.year] = []
      }
      groups[payment.year].push(payment)
    })

    return Object.keys(groups)
      .sort((a, b) => Number(a) - Number(b))
      .map((year) => {
        const payments = groups[Number(year)]
        const totals = payments.reduce(
          (acc, payment) => ({
            payment: acc.payment + payment.payment,
            principal: acc.principal + payment.principal,
            interest: acc.interest + payment.interest,
            endingBalance: payment.balance, // Last payment's balance
          }),
          { payment: 0, principal: 0, interest: 0, endingBalance: 0 }
        )
        return {
          year: Number(year),
          payments,
          totals,
        }
      })
  }, [paymentSchedule])

  const monthlyTax = debouncedPropertyTax / 12
  const monthlyInsurance = debouncedHomeInsurance / 12
  const monthlyOther = debouncedOtherCosts / 12
  const totalMonthlyPayment =
    calculateMortgage.monthlyPayment +
    monthlyTax +
    monthlyInsurance +
    monthlyOther +
    debouncedExtraMonthlyPay

  // Calculate total dollar amount over the life of the loan
  // Include extra payments in the calculation
  const totalDollarAmount = useMemo(() => {
    const numberOfPayments = debouncedLoanTerm * 12
    const baseMonthly = calculateMortgage.monthlyPayment + monthlyTax + monthlyInsurance + monthlyOther

    // Calculate months from start until extra monthly payment begins
    const monthsUntilExtraMonthly = Math.max(0, (extraMonthlyFromYear - startYear) * 12 + (extraMonthlyFromMonth - startMonth))
    const monthsWithExtraMonthly = Math.max(0, numberOfPayments - monthsUntilExtraMonthly)
    const totalExtraMonthlyPayments = debouncedExtraMonthlyPay > 0 ? debouncedExtraMonthlyPay * monthsWithExtraMonthly : 0

    // Calculate months from start until extra yearly payment begins
    const monthsUntilExtraYearly = Math.max(0, (extraYearlyFromYear - startYear) * 12 + (extraYearlyFromMonth - startMonth))
    const yearsWithExtraYearly = Math.max(0, Math.floor((numberOfPayments - monthsUntilExtraYearly) / 12))
    const totalExtraYearlyPayments = debouncedExtraYearlyPay > 0 ? debouncedExtraYearlyPay * yearsWithExtraYearly : 0

    return baseMonthly * numberOfPayments + totalExtraMonthlyPayments + totalExtraYearlyPayments + debouncedExtraOneTimePay
  }, [
    debouncedLoanTerm,
    calculateMortgage.monthlyPayment,
    monthlyTax,
    monthlyInsurance,
    monthlyOther,
    startYear,
    startMonth,
    debouncedExtraMonthlyPay,
    extraMonthlyFromYear,
    extraMonthlyFromMonth,
    debouncedExtraYearlyPay,
    extraYearlyFromYear,
    extraYearlyFromMonth,
    debouncedExtraOneTimePay,
  ])

  // Track changes to trigger border/shadow animation
  const [highlightTrigger, setHighlightTrigger] = useState(0)
  const prevValuesRef = useRef({
    monthlyPayment: calculateMortgage.monthlyPayment,
    monthlyTax,
    monthlyInsurance,
    monthlyOther,
    totalMonthlyPayment,
  })

  useEffect(() => {
    const currentValues = {
      monthlyPayment: calculateMortgage.monthlyPayment,
      monthlyTax,
      monthlyInsurance,
      monthlyOther,
      totalMonthlyPayment,
    }

    // Check if any value changed significantly
    const hasChanged = Object.keys(currentValues).some(
      (key) =>
        Math.abs(
          currentValues[key as keyof typeof currentValues] -
          prevValuesRef.current[key as keyof typeof prevValuesRef.current]
        ) > 0.01
    )

    if (hasChanged) {
      setHighlightTrigger((prev) => prev + 1)
      prevValuesRef.current = currentValues
    }
  }, [
    calculateMortgage.monthlyPayment,
    monthlyTax,
    monthlyInsurance,
    monthlyOther,
    totalMonthlyPayment,
  ])

  const pieChartData = useMemo(() => [
    {
      label: "Principal & Interest",
      value: Math.round(calculateMortgage.monthlyPayment),
      color: "#3b82f6",
    },
    {
      label: "Taxes",
      value: Math.round(monthlyTax),
      color: "#10b981",
    },
    {
      label: "Insurance",
      value: Math.round(monthlyInsurance),
      color: "#f59e0b",
    },
    {
      label: "Other Costs",
      value: Math.round(monthlyOther),
      color: "#ef4444",
    },
    ...(debouncedExtraMonthlyPay > 0 ? [{
      label: "Extra Monthly Pay",
      value: Math.round(debouncedExtraMonthlyPay),
      color: "#8b5cf6",
    }] : []),
  ], [calculateMortgage.monthlyPayment, monthlyTax, monthlyInsurance, monthlyOther, debouncedExtraMonthlyPay])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <div>
        <h1 className="text-3xl font-bold mb-2">Mortgage Calculator</h1>
        <p className="text-gray-600">
          Calculate your monthly mortgage payment and view payment schedule
        </p>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Side - Calculator */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1, duration: 0.3 }}
          className="bg-white rounded-lg border border-gray-200 p-6 space-y-6"
        >
          <h2 className="text-xl font-semibold mb-4">Loan Details</h2>

          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                Loan Amount
              </label>
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                  $
                </span>
                <Input
                  type="number"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(Number(e.target.value))}
                  className="pl-8"
                  min="0"
                />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                Interest Rate (%)
              </label>
              <div className="relative flex-1">
                <NumberPlusMinus
                  value={interestRate}
                  onChange={setInterestRate}
                  minValue={0}
                  maxValue={100}
                  step={0.01}
                />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                Loan Term (years)
              </label>
              <div className="relative flex-1">
                <Input
                  type="number"
                  value={loanTerm}
                  onChange={(e) => setLoanTerm(Number(e.target.value))}
                  min="1"
                  max="50"
                />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                Start Date
              </label>
              <div className="relative flex-1 flex gap-2">
                <select
                  value={startMonth}
                  onChange={(e) => setStartMonth(Number(e.target.value))}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  {monthNames.map((month, index) => (
                    <option key={index} value={index + 1}>
                      {month}
                    </option>
                  ))}
                </select>
                <select
                  value={startYear}
                  onChange={(e) => setStartYear(Number(e.target.value))}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Additional Costs</h3>

              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                    Property Tax (annual)
                  </label>
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      $
                    </span>
                    <Input
                      type="number"
                      value={propertyTax}
                      onChange={(e) => setPropertyTax(Number(e.target.value))}
                      className="pl-8"
                      min="0"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                    Home Insurance (annual)
                  </label>
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      $
                    </span>
                    <Input
                      type="number"
                      value={homeInsurance}
                      onChange={(e) => setHomeInsurance(Number(e.target.value))}
                      className="pl-8"
                      min="0"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                    Other Costs (annual)
                  </label>
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      $
                    </span>
                    <Input
                      type="number"
                      value={otherCosts}
                      onChange={(e) => setOtherCosts(Number(e.target.value))}
                      className="pl-8"
                      min="0"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Extra Payments</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                    Extra Monthly Pay
                  </label>
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      $
                    </span>
                    <Input
                      type="number"
                      value={extraMonthlyPay}
                      onChange={(e) => setExtraMonthlyPay(Number(e.target.value))}
                      className="pl-8"
                      min="0"
                    />
                  </div>
                  <span className="text-sm text-gray-600 whitespace-nowrap">from</span>
                  <select
                    value={extraMonthlyFromMonth}
                    onChange={(e) => setExtraMonthlyFromMonth(Number(e.target.value))}
                    className="flex h-9 w-24 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    {monthNames.map((month, index) => (
                      <option key={index} value={index + 1}>
                        {month}
                      </option>
                    ))}
                  </select>
                  <select
                    value={extraMonthlyFromYear}
                    onChange={(e) => setExtraMonthlyFromYear(Number(e.target.value))}
                    className="flex h-9 w-24 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                    Extra Yearly Pay
                  </label>
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      $
                    </span>
                    <Input
                      type="number"
                      value={extraYearlyPay}
                      onChange={(e) => setExtraYearlyPay(Number(e.target.value))}
                      className="pl-8"
                      min="0"
                    />
                  </div>
                  <span className="text-sm text-gray-600 whitespace-nowrap">from</span>
                  <select
                    value={extraYearlyFromMonth}
                    onChange={(e) => setExtraYearlyFromMonth(Number(e.target.value))}
                    className="flex h-9 w-24 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    {monthNames.map((month, index) => (
                      <option key={index} value={index + 1}>
                        {month}
                      </option>
                    ))}
                  </select>
                  <select
                    value={extraYearlyFromYear}
                    onChange={(e) => setExtraYearlyFromYear(Number(e.target.value))}
                    className="flex h-9 w-24 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                    Extra One-time Pay
                  </label>
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      $
                    </span>
                    <Input
                      type="number"
                      value={extraOneTimePay}
                      onChange={(e) => setExtraOneTimePay(Number(e.target.value))}
                      className="pl-8"
                      min="0"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Right Side - Pie Chart */}
        <motion.div
          key={highlightTrigger}
          animate={{

            borderColor: highlightTrigger > 0
              ? ["rgb(229 231 235)", "rgb(59 130 246)", "rgb(229 231 235)"]
              : "rgb(229 231 235)",
            boxShadow: highlightTrigger > 0
              ? ["0 1px 2px 0 rgb(0 0 0 / 0.05)", "0 10px 15px -3px rgb(59 130 246 / 0.3), 0 4px 6px -4px rgb(59 130 246 / 0.3)", "0 1px 2px 0 rgb(0 0 0 / 0.05)"]
              : "0 1px 2px 0 rgb(0 0 0 / 0.05)",
          }}
          transition={{
            delay: 0.2,
            duration: 0.3,
            borderColor: highlightTrigger > 0 ? {
              duration: 0.8,
              times: [0, 0.3, 1],
            } : {},
            boxShadow: highlightTrigger > 0 ? {
              duration: 0.8,
              times: [0, 0.3, 1],
            } : {},
          }}
          className="bg-white rounded-lg border p-6"
        >
          <h2 className="text-xl font-semibold mb-6">Payment Breakdown</h2>
          <div className="flex flex-col items-center">
            <PieChart data={pieChartData} totalPayment={calculateMortgage.totalPayment} size={350} />
            <div className="mt-6 p-4 bg-gray-50 rounded-lg w-full">
              <div className="space-y-2">
                <div className="flex flex-col gap-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Principal & Interest:</span>
                    <span className="font-semibold">
                      $<AnimatedNumber value={calculateMortgage.monthlyPayment} decimals={2} />
                    </span>
                  </div>
                  {/* <div className="flex justify-end">
                    <span className="text-xs text-gray-500">
                      Total: $<AnimatedNumber value={calculateMortgage.totalPayment} decimals={2} />
                    </span>
                  </div> */}
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Taxes:</span>
                  <span className="font-semibold">
                    $<AnimatedNumber value={monthlyTax} decimals={2} />
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Insurance:</span>
                  <span className="font-semibold">
                    $<AnimatedNumber value={monthlyInsurance} decimals={2} />
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Other Costs:</span>
                  <span className="font-semibold">
                    $<AnimatedNumber value={monthlyOther} decimals={2} />
                  </span>
                </div>
                <div className="pt-2 border-t border-gray-200 flex justify-between">
                  <span className="text-lg font-semibold">Total Monthly:</span>
                  <span className="text-lg font-bold text-blue-600">
                    $<AnimatedNumber value={totalMonthlyPayment} decimals={2} />
                  </span>
                </div>
                <div className="pt-3 border-t-2 border-gray-300 flex justify-between">
                  <span className="text-lg font-bold">Total Amount:</span>
                  <span className="text-xl font-bold text-blue-700">
                    $<AnimatedNumber value={totalDollarAmount} decimals={2} />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Payment Schedule Table */}
      <motion.div
        key={`schedule-${highlightTrigger}`}
        animate={{

          borderColor: highlightTrigger > 0
            ? ["rgb(229 231 235)", "rgb(59 130 246)", "rgb(229 231 235)"]
            : "rgb(229 231 235)",
          boxShadow: highlightTrigger > 0
            ? ["0 1px 2px 0 rgb(0 0 0 / 0.05)", "0 10px 15px -3px rgb(59 130 246 / 0.3), 0 4px 6px -4px rgb(59 130 246 / 0.3)", "0 1px 2px 0 rgb(0 0 0 / 0.05)"]
            : "0 1px 2px 0 rgb(0 0 0 / 0.05)",
        }}
        transition={{
          delay: 0.3,
          duration: 0.6,
          borderColor: highlightTrigger > 0 ? {
            duration: 0.8,
            times: [0, 0.3, 1],
          } : {},
          boxShadow: highlightTrigger > 0 ? {
            duration: 0.8,
            times: [0, 0.3, 1],
          } : {},
        }}
        className="bg-white rounded-lg border p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Payment Schedule</h2>
          <CheckboxDiv
            items={[
              // { value: "months", label: "Months", Icon: CalendarDays, defaultChecked: true },
              // { value: "years", label: "Years", Icon: Calendar },
              { value: "months", label: "Months", defaultChecked: true },
              { value: "years", label: "Years", },
            ]}
            columns={2}
            selectedValues={scheduleView}
            onSelectionChange={(newSelection) => {
              // Make it mutually exclusive - if one is selected, unselect the other
              if (newSelection.size > 1) {
                const lastSelected = Array.from(newSelection).pop()
                setScheduleView(new Set([lastSelected!]))
              } else {
                setScheduleView(newSelection)
              }
            }}
            className="w-auto"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  Date
                </th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  Payment
                </th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  Principal
                </th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  Interest
                </th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  Balance
                </th>
              </tr>
            </thead>
            <tbody>
              {scheduleView.has("months") ? (
                // Monthly view - show all months grouped by year
                yearGroups.map((yearGroup) => (
                  <Fragment key={`year-group-${yearGroup.year}`}>
                    {/* Year Header */}
                    <tr className="bg-blue-50 border-t-2 border-blue-200">
                      <td colSpan={5} className="px-4 py-3 font-bold text-blue-900">
                        {yearGroup.year}
                      </td>
                    </tr>
                    {/* Payments for this year */}
                    {yearGroup.payments.map((row) => (
                      <tr
                        key={row.month}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="px-4 py-3 text-gray-600">{row.date}</td>
                        <td className="px-4 py-3 text-right font-medium">
                          ${row.payment.toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </td>
                        <td className="px-4 py-3 text-right text-green-600">
                          ${row.principal.toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </td>
                        <td className="px-4 py-3 text-right text-red-600">
                          ${row.interest.toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          ${row.balance.toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </td>
                      </tr>
                    ))}
                    {/* Year Totals */}
                    <tr className="bg-gray-100 border-t-2 border-gray-300 font-semibold">
                      <td className="px-4 py-3 text-gray-700">Year {yearGroup.year} Total</td>
                      <td className="px-4 py-3 text-right text-gray-700">
                        ${yearGroup.totals.payment.toLocaleString(undefined, {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </td>
                      <td className="px-4 py-3 text-right text-green-700">
                        ${yearGroup.totals.principal.toLocaleString(undefined, {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </td>
                      <td className="px-4 py-3 text-right text-red-700">
                        ${yearGroup.totals.interest.toLocaleString(undefined, {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700">
                        ${yearGroup.totals.endingBalance.toLocaleString(undefined, {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </td>
                    </tr>
                  </Fragment>
                ))
              ) : (
                // Yearly view - show only year totals (cumulative values)
                yearGroups.map((yearGroup) => (
                  <tr
                    key={`year-${yearGroup.year}`}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 text-gray-600 font-medium">{yearGroup.year}</td>
                    <td className="px-4 py-3 text-right font-medium">
                      ${yearGroup.totals.payment.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </td>
                    <td className="px-4 py-3 text-right text-green-600">
                      ${yearGroup.totals.principal.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </td>
                    <td className="px-4 py-3 text-right text-red-600">
                      ${yearGroup.totals.interest.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      ${yearGroup.totals.endingBalance.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </motion.div>
  )
}
